import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {UpdateOwnableParams} from 'create-components/OwnableConstants';
import navigateToURL, {NavigateToURLOptions} from 'lib/navigateToURL';
import notifications from 'notifications';
import OwnableChoiceTemplate from 'profile/OwnableChoiceTemplate';
import OwnablePage from 'profile/OwnablePage';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';

export interface OwnablesManageViewOptions<
  Ownable extends Backbone.Model,
> extends Backbone.ViewOptions<Ownable> {
  ownableId: string | undefined;
}

type OwnableSaveUpdates = Omit<UpdateOwnableParams, 'owner'> & {owner: string};

type OwnableType = React.ComponentProps<typeof OwnablePage>['ownableType'];

/** Static side of an OwnablesManageView subclass, exposing the bootstrapped collection factory. */
interface OwnablesManageViewConstructor<Ownable extends Backbone.Model> {
  getBootstrappedOwnables(): Backbone.Collection<Ownable>;
}

const ownablesMap: Record<string, Backbone.Collection<Backbone.Model>> = {};

abstract class OwnablesManageView<Ownable extends Backbone.Model> extends Backbone.View<Ownable> {
  /** URL/analytics slug for this ownable type, e.g. `'feature-libraries'`. Set on the subclass prototype. */
  abstract type: string;
  /** ModelTypeConstants value passed to OwnablePage. Set on the subclass prototype. */
  abstract modelType: string;

  ownableId?: string;
  currentOwnable!: Ownable;
  ownables!: Backbone.Collection<Ownable>;

  override events(): Backbone.EventsHash {
    return {
      'click .existing-items a': 'selectOwnableFromList',
      'submit .edit-meta': 'handleSaveSubmit',
      'submit .delete-ownable': 'handleDelete',
    };
  }

  override initialize(options: OwnablesManageViewOptions<Ownable>): void {
    const factory = this.constructor as unknown as OwnablesManageViewConstructor<Ownable>;
    this.ownables = (ownablesMap[this.type] ??
      (ownablesMap[this.type] = factory.getBootstrappedOwnables())) as Backbone.Collection<Ownable>;
    this.ownables.comparator = (ownable: Ownable) => `${ownable.get('owner_handle')}-${ownable.get('name')}`;
    this.listenTo(this.ownables, 'sync', this.renderOwnable.bind(this));
    this.listenTo(this.ownables, 'add remove change:name', this.renderOwnables.bind(this));
    ({ownableId: this.ownableId} = options);
    this.currentOwnable = this.ownables.get(this.ownableId as string);
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
    this.$el.find('.ownable-content').addClass('hidden');
    ReactDOM.unmountComponentAtNode(this.el.querySelector('.react-mount'));
  }

  handleDelete = (): JQueryXHR => {
    const name = this.currentOwnable.get('name');
    const id = this.currentOwnable.get('api_identifier');
    return this.currentOwnable.destroy().done(() => {
      if (this.type === 'feature-libraries') {
        analytics.track('feature_library', 'deleted', {
          product: ANALYTICS_MODULE.MOLBIO,
          actionCategory: ACTION_CATEGORY.WRITE,
          featureLibraryId: id,
        });
      } else {
        analytics.track('data_management', 'deleted_ownable', {type: this.type, name});
      }
      if (this.ownables.length > 0) {
        this.navigateToOwnable(this.ownables.at(0).id);
      } else {
        this.navigateToOwnable(null);
      }
    });
  };

  handleSaveSubmit = (updates: OwnableSaveUpdates): JQueryXHR =>
    this.currentOwnable.save(updates, {patch: true, wait: true}).done(() => {
      if (this.type === 'feature-libraries') {
        analytics.track('feature_library', 'updated', {
          product: ANALYTICS_MODULE.MOLBIO,
          actionCategory: ACTION_CATEGORY.WRITE,
          featureLibraryId: this.currentOwnable.get('api_identifier'),
        });
      } else {
        analytics.track('data_management', 'updated_ownable', {type: this.type});
      }
    });

  createCollaboratorsView(
    collaborators: Array<unknown>,
    {onExport = null}: {onExport?: (() => void) | null} = {}
  ): void {
    renderToDOM(
      <OwnablePage
        ownableType={this.modelType as OwnableType}
        ownable={this.currentOwnable.attributes}
        collaborators={collaborators as React.ComponentProps<typeof OwnablePage>['collaborators']}
        onExport={onExport ?? undefined}
        onRequestSubmit={this.handleSaveSubmit}
        onRequestDelete={this.handleDelete}
      />,
      this.el.querySelector('.react-mount')
    );
  }

  selectOwnableFromList(e: JQueryEventObject): void {
    e.preventDefault();
    const id = $(e.currentTarget).data('ownableId');
    this.navigateToOwnable(id);
  }

  navigateToOwnable(ownableId: string | null, options?: NavigateToURLOptions): void {
    if (window.test_user) {
      return;
    }
    // Assumes ID is last component of URL.
    // eslint-disable-next-line no-restricted-syntax
    const parts = _.initial(window.location.pathname.split('/'));
    parts.push(ownableId != null ? ownableId : '');
    navigateToURL(parts.join('/'), options);
  }

  ownableToHtml(ownable: Ownable): string {
    // oxlint-disable-next-line new-cap
    return OwnableChoiceTemplate(ownable.toJSON());
  }

  renderOwnable(): void {
    if (this.type === 'feature-libraries') {
      analytics.track('feature_library', 'viewed', {
        product: ANALYTICS_MODULE.MOLBIO,
        actionCategory: ACTION_CATEGORY.READ,
        featureLibraryId: this.currentOwnable.get('api_identifier'),
      });
    } else {
      analytics.track('DATA_MANAGEMENT', 'select_ownable', {type: this.type, id: this.currentOwnable.id});
    }
    this.$el.find('.ownable-content').removeClass('hidden');
    this.createCollaboratorsView([]);
  }

  renderOwnables(): void {
    const $chooseButton = this.$el.find('.create-new .dropdown-toggle .dropdown-text');
    const $select = this.$el.find('.create-new .existing-items');
    $select.empty();
    if (this.ownables.models.length > 0) {
      $select.prop('disabled', false);
      for (const ownable of Array.from(this.ownables.models)) {
        const $anchor = $('<a>')
          .attr('href', '#')
          .html(this.ownableToHtml(ownable))
          .data('ownableId', ownable.id);
        const $option = $(
          `<li data-test-component="OwnableActionHeader" data-test-element="existingOwnableOption" data-test-key=${ownable.id}>`
        ).append($anchor);
        if (ownable.id === (this.currentOwnable != null ? this.currentOwnable.id : undefined)) {
          $option.addClass('selected');
        }
        $select.append($option);
      }
      if (this.currentOwnable != null) {
        $chooseButton.html(this.ownableToHtml(this.currentOwnable));
      }
      $chooseButton.parent().removeClass('disabled');
    } else {
      $chooseButton.text('No existing items.').parent().addClass('disabled');
    }
  }

  override render(): this {
    // Ideally this happens in the router, but would require re-implementing the already-hacky
    // navigateToOwnable at the router-level.
    if (this.ownableId != null && this.currentOwnable == null) {
      notifications.error('The item you were looking for no longer exists.');
      this.navigateToOwnable(null, {replace: true});
      return this;
    }

    this.renderOwnables();
    if (this.currentOwnable != null) {
      this.renderOwnable();
    } else {
      this.$el.find('.tab-panel').addClass('hide');
    }

    return this;
  }
}

export default OwnablesManageView;
